import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import clsx from 'clsx';

import { ChatPopup } from './ChatPopup';
import ConversationList from './ConversationList';
import { routes } from 'browser/Routes';
import { AppState } from 'shared/store/reducers';
import './style.less';
import { useAppDispatch, useAppSelector } from 'shared/store';
import { selectActiveVisitor } from 'shared/store/shared-selectors';
import {
  openConversation as openConversationAction,
  closeConversation as closeConversationAction,
} from 'shared/store/global-inbox-slice';

export const GlobalInbox = () => {
  const { pathname } = useLocation();
  const activeRoute = routes.find(config =>
    matchPath(window.location.pathname, config.route),
  );

  const isVisitorPage = useMemo(() => pathname.startsWith('/visitor/'), [pathname]);
  const isSpotlight =
    pathname.startsWith('/spotlight/accounts') ||
    pathname.startsWith('/spotlight/contacts');
  const isAgentPage = activeRoute?.meta?.isAgentPage;
  const isRepOnboarding = activeRoute?.meta?.isRepOnboarding;
  const isSchedulingEventEdit = pathname.startsWith('/scheduling/event-types/edit/');
  const isLiveListen = pathname.startsWith('/livelisten');
  const isSalesfloorRoom = pathname.startsWith('/salesfloor/room/');
  const isAI = pathname.startsWith('/ai') && !pathname.startsWith('/ai/overview');

  const { account } = useSelector((state: AppState) => state.auth);
  const { popups } = useAppSelector(s => s.globalInbox);

  const [widgetSize, setWidgetSize] = useState<'expanded' | 'collapsed'>('collapsed');

  const callStatus = useAppSelector(s => s.realtime.callStatus);
  const visitor = useAppSelector(selectActiveVisitor);
  const dispatch = useAppDispatch();

  const openConversation = useCallback(
    (visitorId: string, conversationId: number) => {
      dispatch(openConversationAction({ visitorId, conversationId }));
    },
    [dispatch],
  );

  const closeConversation = useCallback(() => {
    dispatch(closeConversationAction());
  }, [dispatch]);

  useEffect(() => {
    if (!visitor || !['active', 'established'].includes(callStatus) || isVisitorPage)
      return;
    openConversation(visitor.id, visitor.callConversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callStatus, isVisitorPage]);

  useEffect(() => {
    const expand = () => setWidgetSize('expanded');
    const collapse = () => setWidgetSize('collapsed');
    window.addEventListener('sb:expand', expand);
    window.addEventListener('sb:collapse', collapse);
    return () => {
      window.removeEventListener('sb:expand', expand);
      window.removeEventListener('sb:collapse', collapse);
    };
  }, []);

  const hideGlobalInbox = useMemo(() => {
    if (['active', 'established'].includes(callStatus)) {
      return !account || isVisitorPage || isAgentPage || isRepOnboarding;
    }

    return (
      !account ||
      isVisitorPage ||
      isAgentPage ||
      isRepOnboarding ||
      isSchedulingEventEdit ||
      isSpotlight ||
      isSalesfloorRoom ||
      isLiveListen ||
      isAI
    );
  }, [
    account,
    callStatus,
    isAgentPage,
    isLiveListen,
    isSalesfloorRoom,
    isSchedulingEventEdit,
    isSpotlight,
    isVisitorPage,
    isAI,
    isRepOnboarding,
  ]);

  if (hideGlobalInbox) return null;

  return (
    <div className={clsx('GlobalInbox', `GlobalInbox--${widgetSize}`)}>
      <ConversationList openConversation={openConversation} />
      {Object.entries(popups).map(([visitorId, conversationId]) => {
        return (
          <ChatPopup
            key={visitorId}
            visitorId={visitorId}
            conversationId={conversationId}
            openConversation={openConversation}
            closeConversation={closeConversation}
          />
        );
      })}
    </div>
  );
};
